//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import axios from './axios';
import { useConfig } from '../Config';
import Q from 'q';
import async from 'async';
import { User } from 'oidc-client-ts';
import { ResponseType } from 'axios';
import { getSafeToken } from './securityUtils';
import {
  LoopResponse,
  CreateLoopResponse,
  CreateLoopResult,
  LoopTaskResponse,
  LoopTaskStatusResponse,
  DashboardResponse,
  LoopRetryResponse,
  LoopLinks,
  AISearchResponse,
  Loop,
} from '../components/Sidebar/SidebarTypes';

export interface LearningLoopLimitStatus {
  teamSpaceLimitReached: boolean;
  globalLimitReached: boolean;
  teamSpaceCount: number;
  teamSpaceLimit: number;
  globalCount: number;
  globalLimit: number;
}

type JSONPrimitive = string | number | boolean;
type JSONObject = { [key: string]: JSONValue };
type JSONArray = JSONValue[];
type JSONValue = JSONPrimitive | JSONObject | JSONArray | undefined;

interface QueueOptions {
  method: string;
  url: string;
  body: JSONValue;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  form: FormData;
  deferred: Q.Deferred<any>;
  responseType: ResponseType;
}

// Centralized default page size for list endpoints returning loops.
// Using a constant avoids hardcoding magic numbers across methods and
// makes it easy to tune pagination in one spot if requirements change.
const DEFAULT_PAGE_SIZE = 999;

// Sanitize pagination-related query parameters to prevent negative or malformed
// values from reaching the backend, which could trigger SQL errors that leak
// internal details (NEXUS00006084).
function sanitizePaginationParams(params: { [key: string]: string }): {
  [key: string]: string;
} {
  const sanitized = { ...params };
  const numericKeys = [
    'size',
    'page',
    'limit',
    'offset',
    'pageSize',
    'pageNumber',
  ];
  // Keys that must be >= 1 (1-based pagination)
  const oneBasedKeys = new Set(['page', 'pageNumber']);
  for (const key of numericKeys) {
    if (sanitized[key] !== undefined) {
      const parsed = Number(sanitized[key]);
      const minValue = oneBasedKeys.has(key) ? 1 : 0;
      if (isNaN(parsed) || parsed < minValue || !Number.isFinite(parsed)) {
        delete sanitized[key];
      } else {
        sanitized[key] = Math.floor(parsed).toString();
      }
    }
  }
  return sanitized;
}

// Helper to ensure the size parameter is always included for list-style endpoints.
function withDefaultSize(params: { [key: string]: string } = {}): {
  [key: string]: string;
} {
  if (!params.size) {
    return { ...params, size: DEFAULT_PAGE_SIZE.toString() };
  }
  return params;
}

/**
 * Prepares parameters to be sent as part of the URL, they must be encoded as a UTF-8 string.
 * Parameters are joined together using '&' and a '=' separates parameter names from their values
 *
 * @param {object} parameters - contains the parameters and their values
 *
 * @returns a conversion of a list of parameters into a UTF-8 string that can be tacked onto the end of a URL sent in an HTTP request
 */
function serializeQueryParams(parameters: { [key: string]: string }) {
  const sanitized = sanitizePaginationParams(parameters);
  const orderedKeys: string[] = [];
  // Always put showAll first if present
  if (sanitized.showAll !== undefined) {
    orderedKeys.push('showAll');
  }
  // Add searchString second if present
  if (sanitized.searchString !== undefined) {
    orderedKeys.push('searchString');
  }
  // Append remaining keys (excluding ones already added)
  for (const key in sanitized) {
    if (sanitized.hasOwnProperty(key) && !orderedKeys.includes(key)) {
      orderedKeys.push(key);
    }
  }
  return orderedKeys
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(sanitized[k]))
    .join('&');
}

class LoopDataService {
  static instance: LoopDataService;
  private readonly queueAPICalls: async.QueueObject<unknown>;
  constructor(getUserContext: () => User, getIsAuthenticated: () => boolean) {
    const { services } = useConfig();
    if (!LoopDataService.instance) {
      LoopDataService.instance = this;
    }

    this.queueAPICalls = async.queue((options: QueueOptions, callback) => {
      LoopDataService.instance._request(
        options.method,
        options.url,
        options.body,
        options.headers,
        options.queryParameters,
        options.form,
        options.deferred,
        function () {
          callback();
        },
        getUserContext,
        getIsAuthenticated,
        services?.loop?.url,
        options.responseType,
      );
    }, 3);
  }

  /**
   * The main request method that all API method will eventually call.
   *
   * @param method - http method
   * @param url - url to do request
   * @param body - body parameters / object
   * @param headers - header parameters
   * @param queryParameters - querystring parameters
   * @param form - form data object
   * @param deferred - promise object
   * @param completeJob - Call back to queue to communicate that job is done and the queue can pick up new job.
   * @param getUserContext - Function that returns user object to get access token from
   * @param getIsAuthenticated - Function that returns boolean value determining if user is currently authenticated
   * @param baseUrl - Request base url
   * @param responseType - Expected type of the response to the HTTP request, defaults to JSON
   */
  public _request(
    method: string,
    url: string,
    body: JSONValue,
    headers: { [key: string]: string },
    queryParameters: { [key: string]: string },
    form: FormData,
    deferred: Q.Deferred<any>,
    completeJob: () => void,
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    baseUrl: string | undefined,
    responseType: ResponseType = 'json',
  ) {
    const user = getUserContext();
    const isAuthenticated = getIsAuthenticated();
    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? '?' + queryParams : '');
    // if user is logged in
    headers['Authorization'] = 'Bearer ' + getSafeToken(user.access_token);
    if (!isAuthenticated) {
      deferred.reject('User is not logged in.');
      completeJob();
    }
    if (body && !Object.keys(body).length && !Array.isArray(body)) {
      body = {};
    }
    axios({
      method,
      url: urlWithParams,
      baseURL: baseUrl,
      headers,
      responseType: responseType,
      // GET requests must not carry a body. Passing an object (even `{}`) makes
      // axios auto-attach `Content-Type: application/json` plus an empty JSON
      // body to every read; `undefined` keeps reads body- and Content-Type-free.
      // (The leading-space ' POST' is an intentional multipart/upload sentinel.)
      data:
        method === ' POST' && form ? form : method === 'GET' ? undefined : body,
    })
      .then((response) => {
        deferred.resolve(response.data || response);
        completeJob();
      })
      .catch((error) => {
        deferred.reject(error.response);
        completeJob();
      });
  }

  public request<T>(
    method: string,
    url: string,
    body: JSONValue,
    headers: JSONValue,
    queryParameters: JSONValue,
    form: JSONValue,
    deferred: Q.Deferred<T>,
    responseType = 'json',
  ) {
    this.queueAPICalls.push({
      method: method,
      url: url,
      body: body,
      headers: headers,
      queryParameters: queryParameters,
      form: form,
      deferred: deferred,
      responseType: responseType,
    });
  }

  getLoop(teamspaceId: string | undefined) {
    const deferred = Q.defer();
    this.request(
      'GET',
      '/spaces/' + teamspaceId + '/loops',
      {},
      {},
      withDefaultSize({ showDisabled: 'true' }),
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<LoopResponse>;
  }

  /**
   * New filtered endpoints (NEXUS00003159)
   * Backend now supports server-side filtering so we expose helper methods.
   */
  getLoopsAll(teamspaceId: string | undefined) {
    const deferred = Q.defer<LoopResponse>();
    this.request(
      'GET',
      `/spaces/${teamspaceId}/loops`,
      {},
      {},
      withDefaultSize({ showDisabled: 'true' }),
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<LoopResponse>;
  }

  getLoopsRecent(teamspaceId: string | undefined) {
    const deferred = Q.defer<LoopResponse>();
    this.request(
      'GET',
      `/spaces/${teamspaceId}/loops`,
      {},
      {},
      withDefaultSize({ completed: 'true', enabled: 'true', recent: 'true' }),
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<LoopResponse>;
  }

  getLoopsDisabled(teamspaceId: string | undefined) {
    const deferred = Q.defer<LoopResponse>();
    this.request(
      'GET',
      `/spaces/${teamspaceId}/loops`,
      {},
      {},
      withDefaultSize({ enabled: 'false' }),
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<LoopResponse>;
  }

  getLoopsIncomplete(teamspaceId: string | undefined) {
    const deferred = Q.defer<LoopResponse>();
    this.request(
      'GET',
      `/spaces/${teamspaceId}/loops`,
      {},
      {},
      withDefaultSize({ completed: 'false', enabled: 'true' }),
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<LoopResponse>;
  }

  getLoopUser(teamspaceId: string | undefined) {
    const deferred = Q.defer();
    this.request(
      'GET',
      '/spaces/' + teamspaceId + '/loops',
      {},
      {},
      withDefaultSize({ showDisabled: 'true' }),
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<LoopResponse>;
  }

  createLoop(loopData: Partial<CreateLoopResponse>): Promise<CreateLoopResult> {
    const deferred = Q.defer<CreateLoopResult>();
    const url = '/spaces/' + loopData.teamSpaceId + '/loops';
    const queryParameters = {
      provision: 'true',
    };

    const bodyData = {
      tenantId: loopData.tenantId,
      teamSpaceId: loopData.teamSpaceId,
      workflowType: 'agile',
      associatedUserIds: loopData.associatedUserIds,
      ...loopData,
    };

    // Only include demoDataType if it has a valid value
    if (
      loopData.demoDataType &&
      ['application'].includes(loopData.demoDataType.toLowerCase())
    ) {
      bodyData.demoDataType = loopData.demoDataType;
    }

    const requestBody = JSON.stringify(bodyData);

    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<CreateLoopResult>;
  }

  /**
   * Checks the Learning Loop limit status for a team space.
   * Returns current counts and whether limits are reached.
   * Requires ROLE_ADMIN.
   *
   * @param teamspaceId - ID of the teamspace to check limits for
   * @returns Promise with limit status including counts and flags
   */
  checkLearningLoopLimits(
    teamspaceId: string,
  ): Promise<LearningLoopLimitStatus> {
    const deferred = Q.defer<LearningLoopLimitStatus>();
    const url = `/spaces/${teamspaceId}/loopcount`;

    this.request('GET', url, {}, {}, {}, {}, deferred);

    return deferred.promise as unknown as Promise<LearningLoopLimitStatus>;
  }

  /**
   * Disables a loop by setting its enabled property to false.
   * Requires ROLE_ADMIN.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop to disable
   * @returns Promise with the updated loop metadata
   */
  disableLoop(teamspaceId: string, loopId: string): Promise<LoopResponse> {
    const deferred = Q.defer<LoopResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}`;

    this.request(
      'DELETE',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopResponse>;
  }

  /**
   * Enables a loop by setting its enabled property to true.
   * Requires ROLE_ADMIN.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop to enable
   * @returns Promise with the updated loop metadata
   */
  enableLoop(teamspaceId: string, loopId: string): Promise<LoopResponse> {
    const deferred = Q.defer<LoopResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}`;

    this.request(
      'PATCH',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopResponse>;
  }

  /**
   * Fetches a loop
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop to fetch
   * @returns Promise with the loop metadata
   */
  fetchALoop(
    teamspaceId: string | null | undefined,
    loopId: string | null,
    queryParameters: { [key: string]: string } = {},
  ): Promise<Loop> {
    const deferred = Q.defer<Loop>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}`;

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<Loop>;
  }

  /**
   * Fetch loop members (users associated with a loop).
   * Expected backend response shape per user:
   * {
   *   userId: string;
   *   email: string;
   *   fullName: string | null;
   *   role: 'ROLE_ADMIN' | 'ROLE_USER';
   *   otherLoops: string[] // array of loop names (
   * }
   *
   * @param teamspaceId - ID of the teamspace context
   * @param loopId - ID of the loop whose members are requested
   * @returns Promise resolving to an array of loop member objects
   */
  getLoopMembers(
    teamspaceId: string | undefined,
    loopId: string | undefined,
  ): Promise<any[]> {
    const deferred = Q.defer<any[]>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/members`;

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<any[]>;
  }

  /**
   * Creates a loop task. Hardcoded with "runOnce=true" to prevent re-execution.
   *
   * @param {string} teamspaceId The ID of the teamspace context.
   * @param {(string | undefined)} loopId The ID of the loop for the task.
   * @param {string} type The type of task to create.
   * @param {boolean} async If true, task is processed asynchronously.
   * @returns {Promise<LoopTaskResponse>} Promise with the server task response.
   */
  loopTaskCreation(
    teamspaceId: string,
    loopId: string | undefined,
    type: string,
    async: boolean,
  ): Promise<LoopTaskResponse> {
    const deferred = Q.defer<LoopTaskResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/tasks`;
    const queryParameters = {
      runOnce: 'true',
    };
    const requestBody = JSON.stringify({
      type: type,
      async: async,
    });

    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopTaskResponse>;
  }

  /**
   * Creates a re-runnable loop task for updates by omitting the "runOnce" parameter.
   *
   * @param {string} teamspaceId The ID of the teamspace context.
   * @param {(string | undefined)} loopId The ID of the loop for the task.
   * @param {string} type The type of task to create.
   * @param {boolean} async If true, task is processed asynchronously.
   * @returns {Promise<LoopTaskResponse>} Promise with the server task response.
   */
  loopTaskUpdate(
    teamspaceId: string,
    loopId: string | undefined,
    type: string,
    async: boolean,
  ): Promise<LoopTaskResponse> {
    const deferred = Q.defer<LoopTaskResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/tasks`;
    const queryParameters = {};
    const requestBody = JSON.stringify({
      type: type,
      async: async,
    });

    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopTaskResponse>;
  }

  modifyLoop(
    teamspaceId: string,
    loopId: string | undefined,
    associatedUserIds: string[],
  ): Promise<LoopResponse> {
    const deferred = Q.defer<LoopResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}`;
    const requestBody = JSON.stringify({
      associatedUserIds: associatedUserIds,
    });

    this.request(
      'PUT',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopResponse>;
  }

  loopUniqueName(teamspaceId: string, prefix: string): Promise<LoopResponse> {
    const deferred = Q.defer<LoopResponse>();
    const url = `loops/search/by-prefix`;
    const queryParameters = {
      teamSpaceId: teamspaceId,
      prefix: prefix,
    };
    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<LoopResponse>;
  }

  getLoopCountTenant(showDisabled: boolean = true, tenantId: string) {
    const deferred = Q.defer();
    const queryParameters = {
      showDisabled: showDisabled.toString(),
      tenantId: tenantId,
    };

    this.request('GET', '/loops', {}, {}, queryParameters, {}, deferred);
    return deferred.promise as Q.Promise<LoopResponse>;
  }
  getLoopCountUser(showDisabled: boolean = true, userId: string) {
    const deferred = Q.defer();
    const queryParameters = withDefaultSize({
      showDisabled: showDisabled.toString(),
      userId: userId,
    });

    this.request('GET', '/loops', {}, {}, queryParameters, {}, deferred);
    return deferred.promise as Q.Promise<LoopResponse>;
  }
  getAllLoopList() {
    const deferred = Q.defer<LoopResponse>();
    this.request('GET', '/loops', {}, {}, {}, {}, deferred);
    return deferred.promise
      .then((resp) => resp as LoopResponse)
      .catch((error) => {
        console.error('[LoopDataService] getAllLoopList failed:', error);
        throw error;
      }) as Q.Promise<LoopResponse>;
  }

  /**
   * Fetches the task sequence status for a specific teamspace.
   *
   * @param teamspaceId - ID of the teamspace
   * @param tier - Tier parameter for the task sequence
   * @returns Promise with the task sequence status
   */
  /**
   * Fetches the task sequence status for a specific teamspace.
   *
   * @param teamspaceId - ID of the teamspace
   * @param tier - Tier parameter for the task sequence
   * @returns Promise with the task sequence status
   */
  loopTaskStatus(
    teamspaceId: string,
    tier: string,
    demoDataType: string,
  ): Q.Promise<LoopTaskStatusResponse> {
    const deferred = Q.defer<LoopTaskStatusResponse>();
    const url = `/spaces/${teamspaceId}/tasksequence`;
    const queryParameters: { [key: string]: string } = {
      tier: tier,
    };

    if (demoDataType && ['application'].includes(demoDataType.toLowerCase())) {
      queryParameters.demoDataType = demoDataType;
    }

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise;
  }

  /**
   * Fetches the loop links for a specific loop.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @returns Promise with the loop links
   */

  loopLink(teamspaceId: string, loopId: string): Promise<LoopLinks> {
    const deferred = Q.defer<LoopLinks>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/links`;

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopLinks>;
  }

  /**
   * Fetches the dashboard URL for a specific loop.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @returns Promise with the dashboard URL
   */

  getDashboardUrl(
    teamspaceId: string,
    loopId: string,
  ): Promise<DashboardResponse> {
    const deferred = Q.defer<DashboardResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/dashboard`;

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<DashboardResponse>;
  }

  retryFailedLoopTask(
    teamspaceId: string | undefined,
    loopId: string | null,
  ): Promise<LoopRetryResponse> {
    const deferred = Q.defer<LoopRetryResponse>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/retry`;

    this.request(
      'POST',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<LoopRetryResponse>;
  }

  executeAnalyticsSearch(
    teamspaceId: string,
    searchString: string,
  ): Promise<AISearchResponse> {
    const deferred = Q.defer<AISearchResponse>();
    const url = `/spaces/${teamspaceId}/search`;

    this.request(
      'POST',
      url,
      { searchString: searchString },
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<AISearchResponse>;
  }

  /**
   * Triggers a Loop upgrade via the Loop Genie task.
   * Backend endpoint: POST /loops/{loopId}/upgrade
   * No request body is required.
   */
  upgradeLoop(loopId: string): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/loops/${loopId}/upgrade`;

    this.request(
      'POST',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Fetches the upgrade task preview for a loop.
   * Backend endpoint: GET /loops/{loopId}/upgrade/tasks
   */
  getUpgradeTaskPreview(loopId: string): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/loops/${loopId}/upgrade/tasks`;

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Fetches issues from a specific loop.
   * Falls back to mock data in case of API failure.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @param searchString - Search string to filter results (default: "")
   * @returns Promise with the issues data
   */
  fetchIssues(
    teamspaceId: string,
    loopId: string,
    searchString: string = '',
    showAll: boolean = true,
  ): Promise<any> {
    const deferred = Q.defer();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/plan/issues`;

    const queryParameters: { [key: string]: string } = {
      searchString: searchString,
    };
    if (showAll) {
      queryParameters.showAll = 'true';
    }
    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise
      .then((response) => response)
      .catch((error) => {
        console.error('fetchIssues failed', error);
        throw error;
      }) as unknown as Promise<any>;
  }

  /**
   * Fetches commits from a specific loop.
   * Falls back to mock data in case of API failure.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @param searchString - Search string to filter results (default: "")
   * @returns Promise with the commits data
   */
  fetchCommits(
    teamspaceId: string,
    loopId: string,
    searchString: string = '',
    showAll: boolean = true,
  ): Promise<any> {
    const deferred = Q.defer();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/control/commits`;

    const queryParameters: { [key: string]: string } = {
      searchString: searchString,
    };
    if (showAll) {
      queryParameters.showAll = 'true';
    }

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise.catch((error) => {
      console.error('fetchCommits failed', error);
      throw error;
    }) as unknown as Promise<any>;
  }

  /**
   * Fetches pull requests from a specific loop.
   * Falls back to mock data in case of API failure.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @param searchString - Search string to filter results (default: "")
   * @returns Promise with the pull requests data
   */
  fetchPullRequests(
    teamspaceId: string,
    loopId: string,
    searchString: string = '',
    showAll: boolean = true,
  ): Promise<any> {
    const deferred = Q.defer();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/control/pullRequests`;

    const queryParameters: { [key: string]: string } = {
      searchString: searchString,
    };
    if (showAll) {
      queryParameters.showAll = 'true';
    }

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise.catch((error) => {
      console.error('fetchPullRequests failed', error);
      throw error;
    }) as unknown as Promise<any>;
  }

  /**
   * Fetches builds from a specific loop.
   * Falls back to mock data in case of API failure.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @param searchString - Search string to filter results (default: "")
   * @returns Promise with the builds data
   */
  fetchBuilds(
    teamspaceId: string,
    loopId: string,
    searchString: string = '',
    showAll: boolean = true,
  ): Promise<any> {
    const deferred = Q.defer();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/build/builds`;

    const queryParameters: { [key: string]: string } = {
      searchString: searchString,
    };
    if (showAll) {
      queryParameters.showAll = 'true';
    }

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise.catch((error) => {
      console.error('fetchBuilds failed', error);
      throw error;
    }) as unknown as Promise<any>;
  }

  /**
   * Fetches deployments from a specific loop.
   * Falls back to mock data in case of API failure.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @param searchString - Search string to filter results (default: "")
   * @returns Promise with the deployments data
   */
  fetchDeployments(
    teamspaceId: string,
    loopId: string,
    searchString: string = '',
    showAll: boolean = true,
  ): Promise<any> {
    const deferred = Q.defer();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/deploy/deployments`;

    const queryParameters: { [key: string]: string } = {
      searchString: searchString,
    };
    if (showAll) {
      queryParameters.showAll = 'true';
    }

    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );

    return deferred.promise.catch((error) => {
      console.error('fetchDeployments failed', error);
      throw error;
    }) as unknown as Promise<any>;
  }

  /**
   * Fetches the loop resources (connections) for a specific loop.
   * Endpoint: GET /spaces/{spaceId}/loops/{loopId}/resources
   * Requires Accept: application/json header.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @returns Promise resolving to the resources response
   */
  getLoopResources(
    teamspaceId: string,
    loopId: string,
  ): Promise<{ resources: any[]; errors?: any[] }> {
    const deferred = Q.defer<{ resources: any[]; errors?: any[] }>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/resources`;

    this.request(
      'GET',
      url,
      {},
      { Accept: 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<{
      resources: any[];
      errors?: any[];
    }>;
  }

  /**
   * Get loop segments for a specific loop
   * GET /spaces/{teamspaceId}/loops/{loopId}/segments
   * Requires Accept: application/json header.
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - UUID of the loop
   * @returns Promise resolving to the segments array
   */
  getLoopSegments(teamspaceId: string, loopId: string): Promise<any[]> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/segments`;

    this.request(
      'GET',
      url,
      {},
      { Accept: 'application/json' },
      withDefaultSize({}), // Fetch all segments (size=999)
      {},
      deferred,
    );

    return deferred.promise.then((response: any) => {
      if (
        response &&
        response._embedded &&
        Array.isArray(response._embedded.segments)
      ) {
        return response._embedded.segments;
      }
      return [];
    }) as unknown as Promise<any[]>;
  }

  /**
   * Get tasks for a specific segment
   * GET /segments/{segmentId}/tasks
   * Requires Accept: application/json header.
   *
   * @param segmentId - UUID of the segment
   * @returns Promise resolving to the tasks array
   */
  getSegmentTasks(segmentId: string): Promise<any[]> {
    const deferred = Q.defer<any>();
    const url = `/segments/${segmentId}/tasks`;

    this.request(
      'GET',
      url,
      {},
      { Accept: 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise.then((response: any) => {
      if (
        response &&
        response._embedded &&
        Array.isArray(response._embedded.tasks)
      ) {
        return response._embedded.tasks;
      }
      return [];
    }) as unknown as Promise<any[]>;
  }

  /**
   * Get resources for a specific segment
   * GET /spaces/{teamspaceId}/loops/{loopId}/segments/{segmentId}/resources
   * Requires Accept: application/json header.
   *
   * @param teamspaceId - UUID of the teamspace
   * @param loopId - UUID of the loop
   * @param segmentId - UUID of the segment
   * @returns Promise resolving to the response with resources array and errors array
   */
  getSegmentResources(
    teamspaceId: string,
    loopId: string,
    segmentId: string,
  ): Promise<{ resources: any[]; errors: any[] }> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/segments/${segmentId}/resources`;

    this.request(
      'GET',
      url,
      {},
      { Accept: 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise.then((response: any) => {
      return {
        resources: Array.isArray(response?.resources) ? response.resources : [],
        errors: Array.isArray(response?.errors) ? response.errors : [],
      };
    }) as unknown as Promise<{ resources: any[]; errors: any[] }>;
  }

  captureAIFeedback(loopId: string, yahOrNay: boolean): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `loops/${loopId}/ai/feedback`;
    const requestBody = JSON.stringify({
      customerQuery: '123', // temporary code, it will be dynamic in future dev
      openSearchQuery: '123', // temporary code, it will be dynamic in future dev
      yahOrNay: yahOrNay,
    });

    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Adds a test project resource to a loop via task creation.
   * Backend expects type=ADD_TEST_PROJECT_TO_LOOP, async=true, and project_name / project_description.
   */
  addTestProjectToLoop(
    teamspaceId: string,
    loopId: string,
    projectName: string,
    projectDescription: string,
    segmentId?: string,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/tasks`;
    const queryParameters = {
      runOnce: 'false',
    };
    // Updated body schema to include properties wrapper and optional segmentId
    const properties: any = {
      projectName: projectName,
      projectDescription: projectDescription,
    };

    const body: any = {
      type: 'ADD_TEST_PROJECT_TO_LOOP',
      async: true,
      properties: properties,
    };

    // Only add segmentId if it's provided and not empty
    if (segmentId && segmentId.trim() !== '') {
      body.segmentId = segmentId;
    }

    const requestBody = JSON.stringify(body);
    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Adds a control repository resource to a loop via task creation.
   * Backend expects type=ADD_CONTROL_REPO_TO_LOOP, async=true, and repo_name.
   */
  addControlRepoToLoop(
    teamspaceId: string,
    loopId: string,
    repoName: string,
    segmentId?: string,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/tasks`;
    const queryParameters = {
      runOnce: 'false',
    };
    // Updated body schema to include properties wrapper and optional segmentId
    const properties: any = {
      repoName: repoName,
    };

    const body: any = {
      type: 'ADD_CONTROL_REPO_TO_LOOP',
      async: true,
      properties: properties,
    };

    // Only add segmentId if it's provided and not empty
    if (segmentId && segmentId.trim() !== '') {
      body.segmentId = segmentId;
    }

    const requestBody = JSON.stringify(body);
    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Adds a segment to a loop via task creation.
   */
  addLoopSegmentToLoop(
    teamspaceId: string,
    loopId: string,
    segmentName: string,
    segmentDescription: string,
    workflowType: string,
    demoDataType: string,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/segments`;
    // Updated body schema to include properties wrapper
    const requestBody = JSON.stringify({
      displayName: segmentName,
      description: segmentDescription,
      workflowType: workflowType,
      demoDataType: demoDataType,
    });
    this.request(
      'POST',
      url,
      requestBody,
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Retries loop segment task.
   * Returns 201 if the failed task was restarted and 200 if all tasks are already completed successfully.
   */
  retryLoopSegmentTask(
    teamspaceId: string,
    loopId: string,
    segmentId: string,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/segments/${segmentId}/retry`;
    this.request(
      'POST',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Fetches the segment task sequence.
   * @param teamspaceId - ID of the teamspace
   * @param loopId - ID of the loop
   * @returns Segment task sequence data
   */
  fetchSegmentTaskSequence(
    teamspaceId: string | undefined,
    loopId: string,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/segment-task-sequence`;
    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Does a GET request with the href provided.
   */
  fetchHref(href: string): Promise<any> {
    const deferred = Q.defer<any>();
    this.request('GET', href, {}, {}, {}, {}, deferred);
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Fetches a loop segment by its name.
   * @param teamspaceId
   * @param loopId
   * @param segmentName
   * @returns - The segment data if found, otherwise zero results will be returned.
   */
  getSegmentWithName(
    teamspaceId: string | null | undefined,
    loopId: string | null | undefined,
    segmentName: string,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}/segments?segmentName=${segmentName}`;
    this.request(
      'GET',
      url,
      {},
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  addUsersToLoop(
    loopId: string,
    usernames: string[],
    async: boolean = true,
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/loops/${loopId}/addUsers`;
    const queryParameters = { async: async.toString() };
    this.request(
      'POST',
      url,
      usernames,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Poll task status by taskId using Spring Data REST endpoint.
   * @param {string} taskId The UUID of the task to poll.
   * @returns {Promise<any>} Promise with the task status response.
   */
  getTaskStatus(taskId: string): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/tasks/${taskId}`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Update loop's user list by replacing the entire associatedUserIds.
   * This is used for member removal before calling addUsersToLoop.
   * @param {string} teamspaceId The teamspace ID.
   * @param {string} loopId The loop UUID.
   * @param {string[]} usernames The complete final list of usernames.
   * @returns {Promise<any>} Promise with the update response.
   */
  updateLoopUsers(
    teamspaceId: string,
    loopId: string,
    usernames: string[],
  ): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/spaces/${teamspaceId}/loops/${loopId}`;
    const body = {
      associatedUserIds: usernames,
    };
    this.request(
      'PUT',
      url,
      body,
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }
}

export default LoopDataService;
